/**
 * Strings owned by the shared console kit (`components/console/*`) and by the console pages that were
 * rebuilt on it. Kept apart from `messages.ts` so the kit can be read, and translated, in one place.
 */
export const consoleMessages = {
  fr: {
    requestForm: {
      stepDetails: 'Destinataire et destination',
      nearestPicked: 'Nous avons choisi le point de dépôt le plus proche de vous. Changez-le sur la carte ou dans la liste si vous préférez.',
      destinationTitle: 'Où va le colis',
      destinationHint: 'Choisissez le point le plus proche du destinataire.'
    },
    request: {
      title: 'Demande du client',
      journey: 'Parcours de la demande',
      stepSubmitted: 'Demande envoyée',
      stepAwaitingAgent: 'En attente de la réponse de l’agent',
      stepAwaitingReview: 'En attente de validation',
      stepAccepted: 'Acceptée — colis créé',
      stepRejected: 'Refusée par l’équipe',
      stepDeclined: 'Refusée par l’agent',
      stepCancelled: 'Annulée par le client',
      stepFee: 'Frais enregistrés',
      stepFeeWaiting: 'En attente du paiement du client',
      stepHandover: 'Remis à l’agent',
      stepCollected: 'Pris par le livreur',
      stepDelivered: 'Livré',
      customer: 'Client',
      recipient: 'Destinataire',
      details: 'Détails de la demande',
      pickupAddress: 'Adresse donnée par le client',
      notes: 'Notes du client',
      cod: 'Montant à encaisser',
      pickupPoint: 'Point de dépôt',
      noPickupPoint: 'Aucun point de dépôt',
      destination: 'Point de livraison',
      notChosen: 'Non choisi',
      deadline: 'Réponse attendue avant {time}',
      deadlinePassed: 'Le délai est dépassé : la demande sera refusée automatiquement.',
      declinedBy: 'Refusée par l’agent',
      timedOut: 'L’agent n’a pas répondu à temps',
      rejectedBecause: 'Motif du refus',
      reviewedBy: 'Traitée par',
      reviewedAt: 'Traitée le',
      openParcel: 'Ouvrir le colis',
      openCustomer: 'Voir le client',
      fee: 'Frais de livraison',
      feeSaved: 'Prix noté',
      feeRecorded: 'Frais enregistrés',
      feeNote: 'Un prix noté ne prouve pas un paiement en ligne.',
      parcelState: 'État du colis',
      trackingCode: 'Référence',
      handoverAt: 'Remis à l’agent le',
      collectedAt: 'Collecté le',
      noParcel: 'Aucun colis n’a encore été créé pour cette demande.',
      approveHint: 'Choisissez le point de livraison (obligatoire) et, si besoin, le point de dépôt.',
      pickupOptional: 'Facultatif : sans point de dépôt, le colis est créé comme un colis normal.',
      clearPickup: 'Sans point de dépôt',
      requestId: 'Demande',
      submittedBy: 'Envoyée par'
    },
    detail: {
      call: 'Appeler',
      whatsapp: 'WhatsApp',
      email: 'E-mail',
      contact: 'Contact',
      parcels: 'Colis',
      payments: 'Paiements',
      drops: 'Dépôts du jour',
      activity: 'Activité',
      access: 'Accès',
      overview: 'Aperçu',
      account: 'Compte',
      wallet: 'Gains',
      joined: 'Inscrit le',
      lastActivity: 'Dernière activité',
      noActivityYet: 'Aucune activité',
      activeDeliveries: 'En transport',
      completedDeliveries: 'Livraisons effectuées',
      dropsToday: 'Dépôts aujourd’hui',
      withdrawable: 'À retirer',
      earned: 'Total gagné',
      pendingWithdrawals: 'Retraits en attente',
      openWithdrawals: 'Voir les retraits',
      noDrops: 'Aucun dépôt aujourd’hui.',
      noActivity: 'Aucune action enregistrée sur ce compte.',
      parcelsTitle: 'Colis',
      totalParcels: 'Total',
      inProgress: 'En cours',
      delivered: 'Livrés',
      issues: 'Problèmes',
      returned: 'Retours',
      cancelled: 'Annulés',
      noPayments: 'Aucun paiement noté pour ce client.',
      paymentsHint: 'Frais notés pour ses envois. Ce n’est pas la preuve d’un paiement en ligne.',
      amount: 'Montant',
      reference: 'Référence',
      date: 'Date',
      edit: 'Modifier',
      editPartner: 'Modifier le partenaire',
      saved: 'Enregistré',
      newPassword: 'Nouveau mot de passe',
      newPasswordHint: 'Laissez vide pour garder l’actuel. 12 caractères au moins.',
      lowCreditThreshold: 'Crédit minimum avant alerte (MAD)',
      packagesCount: 'Colis',
      portal: 'Accès à l’espace partenaire',
      portalOn: 'Accès activé',
      portalOff: 'Accès désactivé',
      actions: {
        admin_livreur_suspended: 'Compte suspendu',
        admin_livreur_reactivated: 'Compte réactivé'
      }
    },
    maps: {
      searchAddress: 'Rechercher une adresse',
      search: 'Rechercher',
      useMyLocation: 'Ma position',
      locating: 'Localisation…',
      locationDenied: 'Position introuvable. Autorisez la localisation ou placez le repère à la main.',
      noResults: 'Aucun résultat',
      pinHint: 'Touchez la carte ou déplacez le repère pour choisir l’endroit exact.',
      pinSet: 'Position enregistrée',
      noPin: 'Aucune position sur la carte',
      useAddress: 'Utiliser cette adresse',
      foundAddress: 'Adresse trouvée',
      clearPin: 'Retirer le repère',
      openInMaps: 'Ouvrir dans Maps',
      directions: 'Itinéraire',
      searchPlaces: 'Rechercher un lieu',
      noLocation: 'Pas de position',
      chooseOnMap: 'Choisir sur la carte',
      selected: 'Sélectionné',
      locationsTitle: 'Lieux',
      noneToShow: 'Aucun emplacement n’est enregistré pour ce colis.',
      kinds: {
        sender: 'Expéditeur',
        collection: 'Point de collecte',
        destination: 'Point de livraison',
        return: 'Point de retour'
      }
    },
    platformConfig: {
      feesTitle: 'Frais facturés',
      feesHint: 'Ce que paient le partenaire et le client pour chaque livraison.',
      earningsTitle: 'Rémunérations versées',
      earningsHint: 'Montants fixes payés à chaque agent et au livreur.',
      operationsTitle: 'Fonctionnement',
      operationsHint: 'Si la collecte est ouverte et combien de temps avant qu’une réservation soit annulée.'
    },
    agentCreate: {
      profileHint: 'L’identité de l’agent et le point où il travaille.',
      docsHint: 'Documents pour valider le compte. La photo et la carte d’identité sont obligatoires.'
    },
    tabs: { overview: 'Aperçu', journey: 'Parcours', returns: 'Retours', proof: 'Preuves', manage: 'Gestion' },
    actions: 'Actions',
    optional: 'optionnel',
    copy: 'Copier',
    copied: 'Copié',
    copyFailed: 'Copie impossible',
    filters: 'Filtres',
    moreFilters: 'Plus de filtres',
    lessFilters: 'Moins de filtres',
    account: 'Compte',
    menu: 'Menu',
    stepDone: 'Terminé',
    summary: 'Récapitulatif',
    notSet: 'Non renseigné',
    viewAll: 'Tout voir',
    change: 'Modifier',
    groups: { partnerWork: 'Activité', partnerAccount: 'Compte' },
    packages: {
      listSummary: '{total} colis · {active} en cours · {issues} avec un problème',
      cod: 'À encaisser',
      noAgent: 'Pas encore d’agent',
      directAddress: 'Adresse de l’expéditeur'
    },
    create: {
      stepsTitle: 'Étapes',
      originTitle: 'Expéditeur et collecte',
      originHint: 'D’où part le colis et qui le remet.',
      recipientTitle: 'Destinataire et destination',
      recipientHint: 'Qui reçoit le colis, et à quel point.',
      optionsTitle: 'Détails de l’envoi',
      optionsHint: 'Références, argent à encaisser et partenaire.',
      reviewTitle: 'Vérification',
      reviewHint: 'Vérifiez tout avant de créer le colis. Vous pouvez changer chaque partie.',
      csvStep: 'Fichier CSV'
    },
    csvImport: {
      title: 'Créer plusieurs colis depuis un fichier',
      intro: 'Téléchargez le modèle, écrivez une ligne par colis, envoyez le fichier et lisez le résultat. Les noms suffisent : aucun identifiant à copier.',
      template: 'Télécharger le modèle',
      columnsTitle: 'Quoi écrire dans chaque colonne',
      columnHeader: 'Colonne',
      whatHeader: 'Quoi écrire',
      col: {
        operator_tracking_ref: 'Facultatif. Votre propre référence. Laissée vide, l’app numérote le colis.',
        collection_mode: 'relay : l’expéditeur remet le colis à un point de dépôt. sender_address : nous collectons chez l’expéditeur. Vide : déduit des autres colonnes.',
        sender_name: 'Obligatoire dans les deux cas. Le nom complet de l’expéditeur.',
        sender_phone: 'Obligatoire dans les deux cas. En mode relay, l’expéditeur reçoit le code de remise sur ce numéro.',
        sender_address: 'Obligatoire pour sender_address. Où le livreur collecte le colis.',
        sender_lat: 'Facultatif, les deux ensemble. La position de l’expéditeur sur la carte : latitude comme 33.5731, longitude comme -7.5898.',
        pickup_point: 'Obligatoire pour relay. Le nom d’un point de dépôt de la liste ci-dessous, ou le téléphone de son agent.',
        delivery_point: 'Obligatoire. Le nom du point de livraison de la liste ci-dessous. Écrivez « Nom, Ville » si deux points ont le même nom.',
        delivery_agent_phone: 'Facultatif. Le téléphone de l’agent qui doit livrer, au lieu du choix automatique.',
        partner: 'Facultatif. Le nom de la société partenaire, exactement comme dans la liste des partenaires.',
        cod_amount_mad: 'Obligatoire. Le montant à encaisser auprès du destinataire, en dirhams. 0 s’il n’y a rien.',
        recipient_name: 'Obligatoire. Le nom complet du destinataire.',
        recipient_phone: 'Obligatoire. Le téléphone du destinataire.',
        recipient_address: 'Obligatoire. Où habite le destinataire, comme il le dirait.'
      },
      validNames: 'Noms valides',
      validNamesHint: 'Copiez un nom exactement comme il est écrit ici.',
      deliveryPoints: 'Points de livraison',
      pickupPoints: 'Points de dépôt qui acceptent des colis',
      noPickupPoints: 'Aucun point de dépôt n’accepte de colis pour le moment : seules les lignes « sender_address » peuvent être importées.',
      partners: 'Partenaires',
      agentLine: '{name} · {phone}',
      downloadRefused: 'Télécharger les lignes refusées',
      refusedHint: 'Corrigez-les dans ce fichier, puis envoyez-le : seules ces lignes seront créées.',
      allCreated: 'Toutes les lignes ont été créées.',
      openParcel: 'Ouvrir le colis'
    },
    payment: {
      method: {
        card: 'Carte bancaire',
        apple_pay: 'Apple Pay',
        google_pay: 'Google Pay',
        paypal: 'PayPal',
        bank_transfer: 'Virement bancaire',
        cash: 'Espèces',
        none: 'Non renseigné'
      },
      paidWith: 'Payé avec',
      transaction: 'Transaction',
      reference: 'Référence',
      proof: 'Reçu',
      viewProof: 'Voir le reçu',
      noProof: 'Pas de reçu',
      topup: {
        title: 'Ajouter du crédit',
        description: 'Choisissez comment payer. Un paiement en ligne est crédité tout de suite ; un virement est crédité quand nous avons vu l’argent.',
        payNow: 'Payer en ligne',
        payNowHint: 'Carte, Apple Pay, Google Pay ou PayPal. Crédité tout de suite.',
        bankTransfer: 'Virement bancaire',
        bankTransferHint: 'Faites le virement, puis donnez-nous sa référence bancaire. Crédité après vérification.',
        payButton: 'Payer {amount}',
        sheetTitle: 'Payer votre recharge',
        sheetHint: 'Paiement de démonstration : aucun argent réel n’est prélevé.',
        receiptHint: 'Votre crédit a été ajouté.',
        done: 'Retour à mon crédit',
        paid: 'Paiement reçu. {amount} ajouté à votre crédit.',
        bankReference: 'Référence bancaire',
        bankReferencePlaceholder: 'La référence écrite sur votre virement',
        bankReferenceRequired: 'Écrivez la référence de votre virement.',
        proofLabel: 'Reçu du virement',
        proofHint: 'Une photo ou un PDF du reçu, 5 Mo maximum. Facultatif, mais la vérification est plus rapide.',
        sendTransfer: 'Envoyer pour validation',
        transferSent: 'Envoyé. Votre crédit est ajouté dès que nous avons vu le virement.',
        amountRequired: 'Écrivez le montant à ajouter.'
      },
      admin: {
        recordMethod: 'Reçu par',
        recordReference: 'Référence',
        recordReferenceHint: 'La référence bancaire ou le numéro du reçu. Facultatif.'
      }
    }
  },
  en: {
    requestForm: {
      stepDetails: 'Recipient and destination',
      nearestPicked: 'We chose the drop-off point nearest to you. Change it on the map or in the list if you prefer.',
      destinationTitle: 'Where the parcel goes',
      destinationHint: 'Choose the point closest to the recipient.'
    },
    request: {
      title: 'Customer request',
      journey: 'Request progress',
      stepSubmitted: 'Request sent',
      stepAwaitingAgent: 'Waiting for the agent’s answer',
      stepAwaitingReview: 'Waiting for review',
      stepAccepted: 'Accepted — parcel created',
      stepRejected: 'Rejected by the team',
      stepDeclined: 'Declined by the agent',
      stepCancelled: 'Cancelled by the customer',
      stepFee: 'Fee recorded',
      stepFeeWaiting: 'Waiting for the customer to pay',
      stepHandover: 'Handed to the agent',
      stepCollected: 'Taken by the courier',
      stepDelivered: 'Delivered',
      customer: 'Customer',
      recipient: 'Recipient',
      details: 'Request details',
      pickupAddress: 'Address given by the customer',
      notes: 'Customer’s notes',
      cod: 'Amount to collect',
      pickupPoint: 'Drop-off point',
      noPickupPoint: 'No drop-off point',
      destination: 'Delivery point',
      notChosen: 'Not chosen',
      deadline: 'Answer expected before {time}',
      deadlinePassed: 'The time is over: the request will be refused automatically.',
      declinedBy: 'Declined by the agent',
      timedOut: 'The agent did not answer in time',
      rejectedBecause: 'Reason for the refusal',
      reviewedBy: 'Handled by',
      reviewedAt: 'Handled on',
      openParcel: 'Open the parcel',
      openCustomer: 'See the customer',
      fee: 'Delivery fee',
      feeSaved: 'Price noted',
      feeRecorded: 'Fee recorded',
      feeNote: 'A noted price is not proof of an online payment.',
      parcelState: 'Parcel status',
      trackingCode: 'Reference',
      handoverAt: 'Handed to the agent on',
      collectedAt: 'Collected on',
      noParcel: 'No parcel has been created for this request yet.',
      approveHint: 'Choose the delivery point (required) and, if needed, the drop-off point.',
      pickupOptional: 'Optional: without a drop-off point the parcel is created as a normal parcel.',
      clearPickup: 'No drop-off point',
      requestId: 'Request',
      submittedBy: 'Sent by'
    },
    detail: {
      call: 'Call',
      whatsapp: 'WhatsApp',
      email: 'Email',
      contact: 'Contact',
      parcels: 'Parcels',
      payments: 'Payments',
      drops: 'Today’s drop-offs',
      activity: 'Activity',
      access: 'Access',
      overview: 'Overview',
      account: 'Account',
      wallet: 'Earnings',
      joined: 'Joined',
      lastActivity: 'Last activity',
      noActivityYet: 'No activity yet',
      activeDeliveries: 'In transit',
      completedDeliveries: 'Deliveries completed',
      dropsToday: 'Drop-offs today',
      withdrawable: 'Withdrawable',
      earned: 'Total earned',
      pendingWithdrawals: 'Pending withdrawals',
      openWithdrawals: 'View withdrawals',
      noDrops: 'No drop-off today.',
      noActivity: 'No action has been recorded on this account.',
      parcelsTitle: 'Parcels',
      totalParcels: 'Total',
      inProgress: 'In progress',
      delivered: 'Delivered',
      issues: 'Issues',
      returned: 'Returns',
      cancelled: 'Cancelled',
      noPayments: 'No payment noted for this customer.',
      paymentsHint: 'Fees noted for their parcels. This is not proof of an online payment.',
      amount: 'Amount',
      reference: 'Reference',
      date: 'Date',
      edit: 'Edit',
      editPartner: 'Edit partner',
      saved: 'Saved',
      newPassword: 'New password',
      newPasswordHint: 'Leave empty to keep the current one. At least 12 characters.',
      lowCreditThreshold: 'Minimum credit before alert (MAD)',
      packagesCount: 'Parcels',
      portal: 'Access to the partner area',
      portalOn: 'Access on',
      portalOff: 'Access off',
      actions: {
        admin_livreur_suspended: 'Account suspended',
        admin_livreur_reactivated: 'Account reactivated'
      }
    },
    maps: {
      searchAddress: 'Search an address',
      search: 'Search',
      useMyLocation: 'My location',
      locating: 'Locating…',
      locationDenied: 'Location not found. Allow location or place the pin by hand.',
      noResults: 'No results',
      pinHint: 'Tap the map or move the pin to choose the exact spot.',
      pinSet: 'Position saved',
      noPin: 'No position on the map',
      useAddress: 'Use this address',
      foundAddress: 'Address found',
      clearPin: 'Remove pin',
      openInMaps: 'Open in Maps',
      directions: 'Directions',
      searchPlaces: 'Search a place',
      noLocation: 'No location',
      chooseOnMap: 'Choose on the map',
      selected: 'Selected',
      locationsTitle: 'Locations',
      noneToShow: 'No location is recorded for this parcel.',
      kinds: {
        sender: 'Sender',
        collection: 'Collection point',
        destination: 'Delivery point',
        return: 'Return point'
      }
    },
    platformConfig: {
      feesTitle: 'Fees charged',
      feesHint: 'What the partner and the customer pay for each delivery.',
      earningsTitle: 'Earnings paid out',
      earningsHint: 'Fixed amounts paid to each agent and the courier.',
      operationsTitle: 'Operations',
      operationsHint: 'Whether pickup is open and how long before a reservation is cancelled.'
    },
    agentCreate: {
      profileHint: 'The agent’s identity and the point where they work.',
      docsHint: 'Documents to confirm the account. A photo and an ID card are required.'
    },
    tabs: { overview: 'Overview', journey: 'Journey', returns: 'Returns', proof: 'Proof', manage: 'Manage' },
    actions: 'Actions',
    optional: 'optional',
    copy: 'Copy',
    copied: 'Copied',
    copyFailed: 'Could not copy',
    filters: 'Filters',
    moreFilters: 'More filters',
    lessFilters: 'Fewer filters',
    account: 'Account',
    menu: 'Menu',
    stepDone: 'Done',
    summary: 'Summary',
    notSet: 'Not set',
    viewAll: 'View all',
    change: 'Change',
    groups: { partnerWork: 'Work', partnerAccount: 'Account' },
    packages: {
      listSummary: '{total} parcels · {active} in progress · {issues} with a problem',
      cod: 'To collect',
      noAgent: 'No agent yet',
      directAddress: 'Sender address'
    },
    create: {
      stepsTitle: 'Steps',
      originTitle: 'Sender and collection',
      originHint: 'Where the parcel starts and who hands it over.',
      recipientTitle: 'Recipient and destination',
      recipientHint: 'Who receives the parcel, and at which point.',
      optionsTitle: 'Shipment details',
      optionsHint: 'References, money to collect and partner.',
      reviewTitle: 'Review',
      reviewHint: 'Check everything before creating the parcel. You can change any part.',
      csvStep: 'CSV file'
    },
    csvImport: {
      title: 'Create many parcels from a file',
      intro: 'Download the template, write one line per parcel, upload the file and read the result. Names are enough: no ids to copy.',
      template: 'Download the template',
      columnsTitle: 'What to write in each column',
      columnHeader: 'Column',
      whatHeader: 'What to write',
      col: {
        operator_tracking_ref: 'Optional. Your own reference. Left empty, the app numbers the parcel.',
        collection_mode: 'relay: the sender hands the parcel to a drop-off point. sender_address: we collect it at the sender\'s address. Empty: read from the other columns.',
        sender_name: 'Required for both ways. The sender\'s full name.',
        sender_phone: 'Required for both ways. In relay mode the sender gets the hand-over code on this number.',
        sender_address: 'Required for sender_address. Where the courier collects the parcel.',
        sender_lat: 'Optional, both together. The sender\'s position on the map: latitude like 33.5731, longitude like -7.5898.',
        pickup_point: 'Required for relay. The name of a drop-off point from the list below, or the phone number of its agent.',
        delivery_point: 'Required. The name of the delivery point from the list below. Write "Name, City" if two share a name.',
        delivery_agent_phone: 'Optional. The phone of the agent who should deliver, instead of the automatic choice.',
        partner: 'Optional. The partner company name, exactly as in the partners list.',
        cod_amount_mad: 'Required. The amount to collect from the recipient, in dirhams. 0 if nothing.',
        recipient_name: 'Required. The recipient\'s full name.',
        recipient_phone: 'Required. The recipient\'s phone.',
        recipient_address: 'Required. Where the recipient lives, as they would say it.'
      },
      validNames: 'Valid names',
      validNamesHint: 'Copy a name exactly as written here.',
      deliveryPoints: 'Delivery points',
      pickupPoints: 'Drop-off points that accept parcels',
      noPickupPoints: 'No drop-off point accepts parcels right now, so only "sender_address" lines can be imported.',
      partners: 'Partners',
      agentLine: '{name} · {phone}',
      downloadRefused: 'Download the refused lines',
      refusedHint: 'Correct them in this file, then upload it: only these lines will be created.',
      allCreated: 'Every line was created.',
      openParcel: 'Open the parcel'
    },
    payment: {
      method: {
        card: 'Bank card',
        apple_pay: 'Apple Pay',
        google_pay: 'Google Pay',
        paypal: 'PayPal',
        bank_transfer: 'Bank transfer',
        cash: 'Cash',
        none: 'Not recorded'
      },
      paidWith: 'Paid with',
      transaction: 'Transaction',
      reference: 'Reference',
      proof: 'Receipt',
      viewProof: 'View receipt',
      noProof: 'No receipt',
      topup: {
        title: 'Add credit',
        description: 'Choose how you pay. An online payment is credited at once; a bank transfer is credited once we have seen the money.',
        payNow: 'Pay online',
        payNowHint: 'Card, Apple Pay, Google Pay or PayPal. Credited at once.',
        bankTransfer: 'Bank transfer',
        bankTransferHint: 'Send the transfer, then give us its bank reference. Credited after we check it.',
        payButton: 'Pay {amount}',
        sheetTitle: 'Pay your top-up',
        sheetHint: 'Demo payment: no real money is taken.',
        receiptHint: 'Your credit has been added.',
        done: 'Back to my credit',
        paid: 'Payment received. {amount} added to your credit.',
        bankReference: 'Bank reference',
        bankReferencePlaceholder: 'The reference written on your transfer',
        bankReferenceRequired: 'Write the reference of your transfer.',
        proofLabel: 'Transfer receipt',
        proofHint: 'A photo or a PDF of the receipt, 5 MB at most. Optional, but it makes the check faster.',
        sendTransfer: 'Send for approval',
        transferSent: 'Sent. Your credit is added once we have seen the transfer.',
        amountRequired: 'Write the amount to add.'
      },
      admin: {
        recordMethod: 'Received by',
        recordReference: 'Reference',
        recordReferenceHint: 'The bank reference or the receipt number. Optional.'
      }
    }
  },
  ar: {
    requestForm: {
      stepDetails: 'المستلم والوجهة',
      nearestPicked: 'اخترنا لك أقرب نقطة إيداع. يمكنك تغييرها من الخريطة أو القائمة.',
      destinationTitle: 'إلى أين يذهب الطرد',
      destinationHint: 'اختر النقطة الأقرب إلى المستلم.'
    },
    request: {
      title: 'طلب الزبون',
      journey: 'مسار الطلب',
      stepSubmitted: 'تم إرسال الطلب',
      stepAwaitingAgent: 'في انتظار رد الوكيل',
      stepAwaitingReview: 'في انتظار المراجعة',
      stepAccepted: 'تم القبول — أُنشئ الطرد',
      stepRejected: 'رفضه الفريق',
      stepDeclined: 'رفضه الوكيل',
      stepCancelled: 'ألغاه الزبون',
      stepFee: 'تم تسجيل الرسوم',
      stepFeeWaiting: 'في انتظار دفع الزبون',
      stepHandover: 'سُلّم إلى الوكيل',
      stepCollected: 'أخذه الموزّع',
      stepDelivered: 'تم التسليم',
      customer: 'الزبون',
      recipient: 'المستلم',
      details: 'تفاصيل الطلب',
      pickupAddress: 'العنوان الذي أعطاه الزبون',
      notes: 'ملاحظات الزبون',
      cod: 'المبلغ المطلوب',
      pickupPoint: 'نقطة الإيداع',
      noPickupPoint: 'بدون نقطة إيداع',
      destination: 'نقطة التسليم',
      notChosen: 'لم يتم اختيارها',
      deadline: 'الرد متوقع قبل {time}',
      deadlinePassed: 'انتهت المهلة: سيُرفض الطلب تلقائيا.',
      declinedBy: 'رفضه الوكيل',
      timedOut: 'لم يرد الوكيل في الوقت المناسب',
      rejectedBecause: 'سبب الرفض',
      reviewedBy: 'عالجه',
      reviewedAt: 'تاريخ المعالجة',
      openParcel: 'افتح الطرد',
      openCustomer: 'عرض الزبون',
      fee: 'رسوم التوصيل',
      feeSaved: 'الثمن المسجل',
      feeRecorded: 'الرسوم المسجلة',
      feeNote: 'تسجيل الثمن ليس إثباتا لدفع إلكتروني.',
      parcelState: 'حالة الطرد',
      trackingCode: 'المرجع',
      handoverAt: 'سُلّم إلى الوكيل في',
      collectedAt: 'تم الاستلام في',
      noParcel: 'لم يتم إنشاء طرد لهذا الطلب بعد.',
      approveHint: 'اختر نقطة التسليم (إلزامية) ونقطة الإيداع عند الحاجة.',
      pickupOptional: 'اختياري: بدون نقطة إيداع يُنشأ الطرد كطرد عادي.',
      clearPickup: 'بدون نقطة إيداع',
      requestId: 'الطلب',
      submittedBy: 'أرسله'
    },
    detail: {
      call: 'اتصال',
      whatsapp: 'واتساب',
      email: 'بريد إلكتروني',
      contact: 'التواصل',
      parcels: 'الطرود',
      payments: 'المدفوعات',
      drops: 'تسليمات اليوم',
      activity: 'النشاط',
      access: 'الدخول',
      overview: 'نظرة عامة',
      account: 'الحساب',
      wallet: 'الأرباح',
      joined: 'تاريخ التسجيل',
      lastActivity: 'آخر نشاط',
      noActivityYet: 'لا يوجد نشاط',
      activeDeliveries: 'قيد النقل',
      completedDeliveries: 'عمليات التوصيل المنجزة',
      dropsToday: 'تسليمات اليوم',
      withdrawable: 'قابل للسحب',
      earned: 'إجمالي الأرباح',
      pendingWithdrawals: 'طلبات سحب معلقة',
      openWithdrawals: 'عرض طلبات السحب',
      noDrops: 'لا توجد تسليمات اليوم.',
      noActivity: 'لم يتم تسجيل أي إجراء على هذا الحساب.',
      parcelsTitle: 'الطرود',
      totalParcels: 'الإجمالي',
      inProgress: 'قيد التنفيذ',
      delivered: 'تم تسليمها',
      issues: 'مشاكل',
      returned: 'مرتجعات',
      cancelled: 'ملغاة',
      noPayments: 'لا توجد مدفوعات مسجلة لهذا الزبون.',
      paymentsHint: 'رسوم مسجلة عن طروده. وهذا ليس إثباتا لدفع إلكتروني.',
      amount: 'المبلغ',
      reference: 'المرجع',
      date: 'التاريخ',
      edit: 'تعديل',
      editPartner: 'تعديل الشريك',
      saved: 'تم الحفظ',
      newPassword: 'كلمة مرور جديدة',
      newPasswordHint: 'اتركه فارغا للإبقاء على الحالية. 12 حرفا على الأقل.',
      lowCreditThreshold: 'الحد الأدنى للرصيد قبل التنبيه (درهم)',
      packagesCount: 'الطرود',
      portal: 'الدخول إلى مساحة الشريك',
      portalOn: 'الدخول مفعّل',
      portalOff: 'الدخول معطّل',
      actions: {
        admin_livreur_suspended: 'تم تعليق الحساب',
        admin_livreur_reactivated: 'تمت إعادة تفعيل الحساب'
      }
    },
    maps: {
      searchAddress: 'ابحث عن عنوان',
      search: 'بحث',
      useMyLocation: 'موقعي',
      locating: 'جارٍ تحديد الموقع…',
      locationDenied: 'تعذر تحديد الموقع. اسمح بالموقع أو ضع العلامة يدويا.',
      noResults: 'لا توجد نتائج',
      pinHint: 'المس الخريطة أو حرّك العلامة لاختيار المكان بدقة.',
      pinSet: 'تم حفظ الموقع',
      noPin: 'لا يوجد موقع على الخريطة',
      useAddress: 'استخدم هذا العنوان',
      foundAddress: 'العنوان الذي تم العثور عليه',
      clearPin: 'إزالة الدبوس',
      openInMaps: 'افتح في الخرائط',
      directions: 'الاتجاهات',
      searchPlaces: 'ابحث عن مكان',
      noLocation: 'بدون موقع',
      chooseOnMap: 'اختر على الخريطة',
      selected: 'محدد',
      locationsTitle: 'المواقع',
      noneToShow: 'لا يوجد أي موقع مسجل لهذا الطرد.',
      kinds: {
        sender: 'المرسل',
        collection: 'نقطة الجمع',
        destination: 'نقطة التسليم',
        return: 'نقطة الإرجاع'
      }
    },
    platformConfig: {
      feesTitle: 'الرسوم المحصلة',
      feesHint: 'ما يدفعه الشريك والزبون عن كل توصيل.',
      earningsTitle: 'المستحقات المدفوعة',
      earningsHint: 'مبالغ ثابتة تُدفع لكل وكيل وللموزّع.',
      operationsTitle: 'التشغيل',
      operationsHint: 'هل خدمة الجمع مفتوحة وكم تنتظر قبل إلغاء الحجز.'
    },
    agentCreate: {
      profileHint: 'هوية الوكيل والنقطة التي يعمل فيها.',
      docsHint: 'وثائق لتأكيد الحساب. الصورة وبطاقة الهوية مطلوبتان.'
    },
    tabs: { overview: 'نظرة عامة', journey: 'المسار', returns: 'المرتجعات', proof: 'الإثباتات', manage: 'الإدارة' },
    actions: 'الإجراءات',
    optional: 'اختياري',
    copy: 'نسخ',
    copied: 'تم النسخ',
    copyFailed: 'تعذر النسخ',
    filters: 'عوامل التصفية',
    moreFilters: 'المزيد من عوامل التصفية',
    lessFilters: 'عوامل تصفية أقل',
    account: 'الحساب',
    menu: 'القائمة',
    stepDone: 'مكتمل',
    summary: 'ملخص',
    notSet: 'غير محدد',
    viewAll: 'عرض الكل',
    change: 'تعديل',
    groups: { partnerWork: 'العمل', partnerAccount: 'الحساب' },
    packages: {
      listSummary: '{total} طرد · {active} جارٍ · {issues} فيها مشكل',
      cod: 'المبلغ المطلوب',
      noAgent: 'لا يوجد وكيل بعد',
      directAddress: 'عنوان المرسل'
    },
    create: {
      stepsTitle: 'الخطوات',
      originTitle: 'المرسل والاستلام',
      originHint: 'من أين ينطلق الطرد ومن يسلّمه.',
      recipientTitle: 'المستلم والوجهة',
      recipientHint: 'من يستلم الطرد وفي أي نقطة.',
      optionsTitle: 'تفاصيل الشحنة',
      optionsHint: 'المراجع والمبلغ المطلوب والشريك.',
      reviewTitle: 'المراجعة',
      reviewHint: 'راجع كل شيء قبل إنشاء الطرد. يمكنك تغيير أي جزء.',
      csvStep: 'ملف CSV'
    },
    csvImport: {
      title: 'إنشاء عدة طرود من ملف',
      intro: 'حمّل النموذج، اكتب سطرا لكل طرد، ارفع الملف واقرأ النتيجة. الأسماء تكفي: لا حاجة لنسخ معرّفات.',
      template: 'تحميل النموذج',
      columnsTitle: 'ماذا تكتب في كل عمود',
      columnHeader: 'العمود',
      whatHeader: 'ماذا تكتب',
      col: {
        operator_tracking_ref: 'اختياري. مرجعك الخاص. إذا تُرك فارغا يرقّم التطبيق الطرد.',
        collection_mode: 'relay: يسلّم المرسل الطرد إلى نقطة إيداع. sender_address: نستلمه من عنوان المرسل. فارغ: يُستنتج من الأعمدة الأخرى.',
        sender_name: 'مطلوب في الحالتين. الاسم الكامل للمرسل.',
        sender_phone: 'مطلوب في الحالتين. في وضع relay يصل رمز التسليم إلى هذا الرقم.',
        sender_address: 'مطلوب مع sender_address. المكان الذي يستلم منه الموزّع الطرد.',
        sender_lat: 'اختياري، الاثنان معا. موقع المرسل على الخريطة: خط العرض مثل 33.5731 وخط الطول مثل -7.5898.',
        pickup_point: 'مطلوب مع relay. اسم نقطة إيداع من القائمة أدناه، أو هاتف وكيلها.',
        delivery_point: 'مطلوب. اسم نقطة التسليم من القائمة أدناه. اكتب "الاسم، المدينة" إذا تشابه اسمان.',
        delivery_agent_phone: 'اختياري. هاتف الوكيل الذي يجب أن يسلّم بدل الاختيار التلقائي.',
        partner: 'اختياري. اسم شركة الشريك كما هو في قائمة الشركاء.',
        cod_amount_mad: 'مطلوب. المبلغ الذي يُحصَّل من المستلم بالدرهم. 0 إذا لا شيء.',
        recipient_name: 'مطلوب. الاسم الكامل للمستلم.',
        recipient_phone: 'مطلوب. هاتف المستلم.',
        recipient_address: 'مطلوب. أين يسكن المستلم كما يقولها هو.'
      },
      validNames: 'الأسماء الصالحة',
      validNamesHint: 'انسخ الاسم كما هو مكتوب هنا.',
      deliveryPoints: 'نقاط التسليم',
      pickupPoints: 'نقاط الإيداع التي تقبل الطرود',
      noPickupPoints: 'لا توجد نقطة إيداع تقبل الطرود حاليا، لذلك تُستورد فقط أسطر "sender_address".',
      partners: 'الشركاء',
      agentLine: '{name} · {phone}',
      downloadRefused: 'تحميل الأسطر المرفوضة',
      refusedHint: 'صححها في هذا الملف ثم ارفعه: ستُنشأ هذه الأسطر فقط.',
      allCreated: 'تم إنشاء كل الأسطر.',
      openParcel: 'فتح الطرد'
    },
    payment: {
      method: {
        card: 'بطاقة بنكية',
        apple_pay: 'Apple Pay',
        google_pay: 'Google Pay',
        paypal: 'PayPal',
        bank_transfer: 'تحويل بنكي',
        cash: 'نقدا',
        none: 'غير مسجّل'
      },
      paidWith: 'طريقة الدفع',
      transaction: 'المعاملة',
      reference: 'المرجع',
      proof: 'الإيصال',
      viewProof: 'عرض الإيصال',
      noProof: 'لا يوجد إيصال',
      topup: {
        title: 'إضافة رصيد',
        description: 'اختر طريقة الدفع. يُضاف الدفع عبر الإنترنت فورا، ويُضاف التحويل البنكي بعد أن نرى المبلغ.',
        payNow: 'الدفع عبر الإنترنت',
        payNowHint: 'بطاقة أو Apple Pay أو Google Pay أو PayPal. يُضاف فورا.',
        bankTransfer: 'تحويل بنكي',
        bankTransferHint: 'أجرِ التحويل ثم أعطنا مرجعه البنكي. يُضاف بعد التحقق.',
        payButton: 'ادفع {amount}',
        sheetTitle: 'ادفع شحن رصيدك',
        sheetHint: 'دفع تجريبي: لا يُسحب أي مال حقيقي.',
        receiptHint: 'تمت إضافة رصيدك.',
        done: 'العودة إلى رصيدي',
        paid: 'تم استلام الدفع. أُضيف {amount} إلى رصيدك.',
        bankReference: 'المرجع البنكي',
        bankReferencePlaceholder: 'المرجع المكتوب على التحويل',
        bankReferenceRequired: 'اكتب مرجع التحويل.',
        proofLabel: 'إيصال التحويل',
        proofHint: 'صورة أو ملف PDF للإيصال، 5 ميغابايت كحد أقصى. اختياري لكنه يسرّع التحقق.',
        sendTransfer: 'إرسال للمصادقة',
        transferSent: 'تم الإرسال. يُضاف رصيدك بمجرد أن نرى التحويل.',
        amountRequired: 'اكتب المبلغ المراد إضافته.'
      },
      admin: {
        recordMethod: 'استُلم عن طريق',
        recordReference: 'المرجع',
        recordReferenceHint: 'المرجع البنكي أو رقم الإيصال. اختياري.'
      }
    }
  },
  ma: {
    requestForm: {
      stepDetails: 'المستلم والوجهة',
      nearestPicked: 'ختارنا ليك أقرب نقطة إيداع. تقدر تبدلها من الخريطة ولا من اللائحة.',
      destinationTitle: 'فين غادي يمشي الكولي',
      destinationHint: 'ختار النقطة الأقرب للمستلم.'
    },
    request: {
      title: 'طلب الزبون',
      journey: 'مسار الطلب',
      stepSubmitted: 'الطلب تصيفط',
      stepAwaitingAgent: 'كنتسناو جواب الكارديان',
      stepAwaitingReview: 'كنتسناو المراجعة',
      stepAccepted: 'تقبل — الكولي تصاوب',
      stepRejected: 'الفريق رفضو',
      stepDeclined: 'الكارديان رفضو',
      stepCancelled: 'الزبون لغاه',
      stepFee: 'الرسوم تسجلات',
      stepFeeWaiting: 'كنتسناو الزبون يخلص',
      stepHandover: 'تسلم للكارديان',
      stepCollected: 'الليفرور خداه',
      stepDelivered: 'توصل',
      customer: 'الزبون',
      recipient: 'المستلم',
      details: 'تفاصيل الطلب',
      pickupAddress: 'العنوان اللي عطا الزبون',
      notes: 'ملاحظات الزبون',
      cod: 'المبلغ اللي خاص يتخلص',
      pickupPoint: 'نقطة الإيداع',
      noPickupPoint: 'بلا نقطة إيداع',
      destination: 'نقطة التسليم',
      notChosen: 'ما تختارتش',
      deadline: 'الجواب مستنى قبل {time}',
      deadlinePassed: 'سالا الوقت: الطلب غادي يترفض بوحدو.',
      declinedBy: 'الكارديان رفضو',
      timedOut: 'الكارديان ما جاوبش فالوقت',
      rejectedBecause: 'سبب الرفض',
      reviewedBy: 'عالجو',
      reviewedAt: 'تعالج فـ',
      openParcel: 'حل الكولي',
      openCustomer: 'شوف الزبون',
      fee: 'رسوم التوصيل',
      feeSaved: 'الثمن اللي تسجل',
      feeRecorded: 'الرسوم المسجلة',
      feeNote: 'تسجيل الثمن ماشي إثبات ديال خلاص أونلاين.',
      parcelState: 'حالة الكولي',
      trackingCode: 'المرجع',
      handoverAt: 'تسلم للكارديان فـ',
      collectedAt: 'تجمع فـ',
      noParcel: 'مازال ما تصاوب حتى كولي لهاد الطلب.',
      approveHint: 'ختار نقطة التسليم (ضرورية) ونقطة الإيداع إلا احتجتي.',
      pickupOptional: 'اختياري: بلا نقطة إيداع كيتصاوب الكولي كولي عادي.',
      clearPickup: 'بلا نقطة إيداع',
      requestId: 'الطلب',
      submittedBy: 'صيفطو'
    },
    detail: {
      call: 'عيط',
      whatsapp: 'واتساب',
      email: 'إيميل',
      contact: 'التواصل',
      parcels: 'الكوليات',
      payments: 'الخلصات',
      drops: 'تسليمات اليوم',
      activity: 'النشاط',
      access: 'الدخول',
      overview: 'نظرة عامة',
      account: 'الحساب',
      wallet: 'الأرباح',
      joined: 'تسجل فـ',
      lastActivity: 'آخر نشاط',
      noActivityYet: 'ما كاين حتى نشاط',
      activeDeliveries: 'فالطريق',
      completedDeliveries: 'توصيلات سالات',
      dropsToday: 'تسليمات اليوم',
      withdrawable: 'اللي تقدر تسحب',
      earned: 'مجموع الأرباح',
      pendingWithdrawals: 'طلبات سحب مستناية',
      openWithdrawals: 'شوف طلبات السحب',
      noDrops: 'ماكاين حتى تسليم اليوم.',
      noActivity: 'ما تسجل حتى إجراء على هاد الحساب.',
      parcelsTitle: 'الكوليات',
      totalParcels: 'المجموع',
      inProgress: 'جارية',
      delivered: 'تسلموا',
      issues: 'مشاكل',
      returned: 'رجوعات',
      cancelled: 'ملغيين',
      noPayments: 'ماكاين حتى خلاص مسجل لهاد الزبون.',
      paymentsHint: 'المصاريف المسجلة على الكوليات ديالو. ماشي إثبات ديال خلاص أونلاين.',
      amount: 'المبلغ',
      reference: 'المرجع',
      date: 'التاريخ',
      edit: 'بدل',
      editPartner: 'بدل الشريك',
      saved: 'تسجل',
      newPassword: 'كلمة سر جديدة',
      newPasswordHint: 'خليه خاوي باش تبقى الحالية. 12 حرف على الأقل.',
      lowCreditThreshold: 'الحد الأدنى د الكريدي قبل التنبيه (درهم)',
      packagesCount: 'الكوليات',
      portal: 'الدخول لبلاصة الشريك',
      portalOn: 'الدخول خدام',
      portalOff: 'الدخول موقوف',
      actions: {
        admin_livreur_suspended: 'الحساب تعلق',
        admin_livreur_reactivated: 'الحساب رجع خدام'
      }
    },
    maps: {
      searchAddress: 'قلب على عنوان',
      search: 'قلب',
      useMyLocation: 'موقعي',
      locating: 'كنحدد الموقع…',
      locationDenied: 'ما قدرناش نلقاو البلاصة. سمح بالموقع ولا حط العلامة بيدك.',
      noResults: 'ما كاين حتى نتيجة',
      pinHint: 'كليكي على الخريطة ولا حرك العلامة باش تختار البلاصة بالضبط.',
      pinSet: 'تسجل الموقع',
      noPin: 'ما كاين حتى موقع فالخريطة',
      useAddress: 'استعمل هاد العنوان',
      foundAddress: 'العنوان لي لقينا',
      clearPin: 'حيد الدبوس',
      openInMaps: 'حل فالخريطة',
      directions: 'الطريق',
      searchPlaces: 'قلب على بلاصة',
      noLocation: 'بلا موقع',
      chooseOnMap: 'ختار من الخريطة',
      selected: 'محدد',
      locationsTitle: 'المواقع',
      noneToShow: 'ما كاين حتى موقع مسجل لهاد الكولي.',
      kinds: {
        sender: 'المرسل',
        collection: 'نقطة التجميع',
        destination: 'نقطة التسليم',
        return: 'نقطة الرجوع'
      }
    },
    platformConfig: {
      feesTitle: 'الرسوم لي كتتخلص',
      feesHint: 'شحال كيخلص الشريك والزبون على كل توصيلة.',
      earningsTitle: 'المستحقات لي كتخلص',
      earningsHint: 'مبالغ ثابتة كتتخلص لكل كارديان وللليفرور.',
      operationsTitle: 'التسيير',
      operationsHint: 'واش خدمة الجمع مفتوحة وشحال كتسنى قبل ما يتلغى الحجز.'
    },
    agentCreate: {
      profileHint: 'هوية الكارديان والنقطة اللي كيخدم فيها.',
      docsHint: 'وثائق باش نأكدو الحساب. التصويرة وبطاقة التعريف ضروريين.'
    },
    tabs: { overview: 'نظرة عامة', journey: 'المسار', returns: 'الرجوعات', proof: 'الإثباتات', manage: 'التسيير' },
    actions: 'الإجراءات',
    optional: 'اختياري',
    copy: 'نسخ',
    copied: 'تنسخ',
    copyFailed: 'ما قدرناش ننسخو',
    filters: 'الفيلترات',
    moreFilters: 'فيلترات أخرى',
    lessFilters: 'فيلترات أقل',
    account: 'الحساب',
    menu: 'المنيو',
    stepDone: 'سالا',
    summary: 'الملخص',
    notSet: 'ماشي محدد',
    viewAll: 'شوف الكل',
    change: 'بدل',
    groups: { partnerWork: 'الخدمة', partnerAccount: 'الحساب' },
    packages: {
      listSummary: '{total} كولي · {active} جاري · {issues} فيها مشكل',
      cod: 'المبلغ اللي خاص يتخلص',
      noAgent: 'مازال ما كاين حتى كارديان',
      directAddress: 'عنوان المرسل'
    },
    create: {
      stepsTitle: 'الخطوات',
      originTitle: 'المرسل والتجميع',
      originHint: 'منين غادي يبدا الكولي وشكون غادي يسلمو.',
      recipientTitle: 'المستلم والوجهة',
      recipientHint: 'شكون غادي يستلم الكولي وفأنهي نقطة.',
      optionsTitle: 'تفاصيل الإرسال',
      optionsHint: 'المراجع، الفلوس اللي خاصها تتجمع والشريك.',
      reviewTitle: 'المراجعة',
      reviewHint: 'شوف كلشي قبل ما تصاوب الكولي. تقدر تبدل أي جزء.',
      csvStep: 'ملف CSV'
    },
    csvImport: {
      title: 'صاوب بزاف د الكوليات من ملف',
      intro: 'نزّل النموذج، كتب سطر لكل كولي، صيفط الملف وقرا النتيجة. السميات كافية: ما كاين حتى معرّف تنسخو.',
      template: 'نزّل النموذج',
      columnsTitle: 'شنو تكتب فكل عمود',
      columnHeader: 'العمود',
      whatHeader: 'شنو تكتب',
      col: {
        operator_tracking_ref: 'اختياري. المرجع ديالك. إيلا خليتيه خاوي التطبيق كيعطي نمرة للكولي.',
        collection_mode: 'relay: المرسل كيسلّم الكولي لنقطة إيداع. sender_address: كنجمعوه من عنوان المرسل. خاوي: كيتفهم من الأعمدة الأخرى.',
        sender_name: 'ضروري فالحالتين. السمية الكاملة ديال المرسل.',
        sender_phone: 'ضروري فالحالتين. فوضع relay المرسل كيوصلو كود التسليم فهاد النمرة.',
        sender_address: 'ضروري مع sender_address. فين كيجمع الموزّع الكولي.',
        sender_lat: 'اختياري، جوج معا. بلاصة المرسل فالخريطة: خط العرض بحال 33.5731 وخط الطول بحال -7.5898.',
        pickup_point: 'ضروري مع relay. سمية نقطة إيداع من اللائحة لتحت، ولا تيليفون الكارديان ديالها.',
        delivery_point: 'ضروري. سمية نقطة التسليم من اللائحة لتحت. كتب "السمية، المدينة" إيلا جوج عندهم نفس السمية.',
        delivery_agent_phone: 'اختياري. تيليفون الكارديان اللي خاصو يسلّم بلاصة الاختيار التلقائي.',
        partner: 'اختياري. سمية شركة الشريك بحال ما هي فلائحة الشركاء.',
        cod_amount_mad: 'ضروري. المبلغ اللي كيتحصّل من المستلم بالدرهم. 0 إيلا ما كاين والو.',
        recipient_name: 'ضروري. السمية الكاملة ديال المستلم.',
        recipient_phone: 'ضروري. تيليفون المستلم.',
        recipient_address: 'ضروري. فين كيسكن المستلم بحال ما غادي يقولها هو.'
      },
      validNames: 'السميات الصحيحة',
      validNamesHint: 'نسخ السمية بحال ما هي مكتوبة هنا.',
      deliveryPoints: 'نقط التسليم',
      pickupPoints: 'نقط الإيداع اللي كتقبل الكولياتات',
      noPickupPoints: 'ما كاينة حتى نقطة إيداع كتقبل الكولياتات دابا، وبالتالي غير سطور "sender_address" اللي تقدر تستورد.',
      partners: 'الشركاء',
      agentLine: '{name} · {phone}',
      downloadRefused: 'نزّل السطور المرفوضة',
      refusedHint: 'صلحهم فهاد الملف، ومن بعد صيفطو: غير هاد السطور غادي تتصاوب.',
      allCreated: 'كاع السطور تصاوبو.',
      openParcel: 'فتح الكولي'
    },
    payment: {
      method: {
        card: 'كارط بنكية',
        apple_pay: 'Apple Pay',
        google_pay: 'Google Pay',
        paypal: 'PayPal',
        bank_transfer: 'تحويل بنكي',
        cash: 'كاش',
        none: 'ما تسجلش'
      },
      paidWith: 'طريقة الأداء',
      transaction: 'المعاملة',
      reference: 'المرجع',
      proof: 'الوصل',
      viewProof: 'شوف الوصل',
      noProof: 'ما كاين وصل',
      topup: {
        title: 'زيد الرصيد',
        description: 'ختار كيفاش تخلص. الأداء أونلاين كيتزاد فالحين، والتحويل البنكي كيتزاد من بعد ما نشوفو الفلوس.',
        payNow: 'خلص أونلاين',
        payNowHint: 'كارط ولا Apple Pay ولا Google Pay ولا PayPal. كيتزاد فالحين.',
        bankTransfer: 'تحويل بنكي',
        bankTransferHint: 'دير التحويل ومن بعد عطينا المرجع البنكي ديالو. كيتزاد من بعد التحقق.',
        payButton: 'خلص {amount}',
        sheetTitle: 'خلص شحن الرصيد',
        sheetHint: 'أداء تجريبي: ما كيتسحب حتى فلوس حقيقية.',
        receiptHint: 'الرصيد ديالك تزاد.',
        done: 'رجع للرصيد ديالي',
        paid: 'توصلنا بالأداء. تزاد {amount} فالرصيد ديالك.',
        bankReference: 'المرجع البنكي',
        bankReferencePlaceholder: 'المرجع المكتوب فالتحويل',
        bankReferenceRequired: 'كتب مرجع التحويل.',
        proofLabel: 'وصل التحويل',
        proofHint: 'تصويرة ولا PDF ديال الوصل، 5 ميغا بحد أقصى. اختياري ولكن كيسرّع التحقق.',
        sendTransfer: 'صيفط للمصادقة',
        transferSent: 'تصيفط. الرصيد ديالك كيتزاد ملي نشوفو التحويل.',
        amountRequired: 'كتب المبلغ اللي بغيتي تزيد.'
      },
      admin: {
        recordMethod: 'توصل بيها',
        recordReference: 'المرجع',
        recordReferenceHint: 'المرجع البنكي ولا نمرة الوصل. اختياري.'
      }
    }
  }
}
